
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Tag } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import ContentSection from '@/components/reader-home/ContentSection';
import TrendingTopics from '@/components/reader-home/TrendingTopics';
import { useTaggedArticles } from '@/hooks/reader-home/useTaggedArticles';
import { useTopicService } from '@/hooks/reader-home/useTopicService';
import { transformToJournalistArticle } from '@/utils/articleTransformUtils';

const TaggedArticlesView: React.FC = () => {
  const { tagName } = useParams<{ tagName: string }>();
  const navigate = useNavigate();
  const [relatedTopics, setRelatedTopics] = React.useState<string[]>([]);

  // Fetch articles by tag
  const { articles, loading, error } = useTaggedArticles(tagName);
  
  // Custom hook to load topics
  const { loadTopics } = useTopicService(setRelatedTopics);
  
  // Load related topics
  React.useEffect(() => {
    loadTopics();
  }, [loadTopics]);

  return (
    <div className="container mx-auto px-4 py-8">
      <Button 
        variant="ghost" 
        className="mb-6" 
        onClick={() => navigate(-1)}
      >
        <ChevronLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      
      <div className="flex items-center mb-8">
        <Tag className="h-6 w-6 mr-2 text-primary" />
        <h1 className="text-3xl font-bold">
          {tagName ? `Articles tagged with "${tagName}"` : 'Tagged Articles'}
        </h1>
      </div>
      
      {error && (
        <div className="p-4 mb-6 bg-destructive/10 text-destructive rounded-md">
          <p>Error loading articles. Please try refreshing the page.</p>
        </div>
      )}
      
      {/* Related topics */}
      <TrendingTopics 
        topics={relatedTopics.filter(topic => topic !== tagName)}
        loading={loading} 
      />
      
      {/* Articles */}
      {loading ? (
        <div className="space-y-4 mt-8">
          <Skeleton className="h-10 w-72" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-[300px] rounded-md" />
            <Skeleton className="h-[300px] rounded-md" />
            <Skeleton className="h-[300px] rounded-md" />
          </div>
        </div>
      ) : (
        <ContentSection 
          title={`${articles.length} Articles Found`}
          articles={articles}
          emptyStateMessage={`No articles found with the tag "${tagName}"`}
          emptyStateAction={
            <Button 
              onClick={() => navigate('/home')}
              variant="outline"
              className="mt-4"
            >
              Return to Home
            </Button>
          }
        />
      )}
    </div>
  );
};

export default TaggedArticlesView;
